import { Component, ElementRef, OnInit, inject, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import {
  MenuItemDto,
  OperationTypeEnum,
  OrgContextDto,
  SessionDto,
  UserClient,
} from '@data-access/bulk-operations-api';
import { datadogRum } from '@datadog/browser-rum';
import {
  LanguagesEnum,
  changeLanguage,
  getLocalePreference,
  getVersionFromBaseUri,
  getVersionFromUrl,
} from '@em-helpers';
import { FeatureFlagService } from '@feature-flag';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CircleQuestionMark, ExternalLink, Power, Sparkles, User } from 'lucide-angular';
import { CookieService } from 'ngx-cookie-service';
import { Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { DisabledOperationService } from '../../core/services/disabled-operation.service';
import { UserSessionService } from '../../core/services/user-session.service';
import { WalkmeService } from '../../core/services/walkme.service';
import { SelectedEmployeeService } from '../../modules/employee/employee-card/services/selected-employee.service';
import { GaiaAssistantComponent } from '../gaia-assistant/gaia-assistant.component';

@Component({
  selector: 'di-header',
  templateUrl: './header.component.html',
  standalone: false,
  host: {
    class: 'tw:block',
  },
})
@UntilDestroy()
export class HeaderComponent implements OnInit {
  userSessionService = inject(UserSessionService);
  private cookieService = inject(CookieService);
  private userClient = inject(UserClient);
  private router = inject(Router);
  private walkmeService = inject(WalkmeService);
  private title = inject(Title);

  readonly icons = { Sparkles, ExternalLink, Power, CircleQuestionMark, User };
  readonly gaiaAssistantChatSignal = viewChild<GaiaAssistantComponent>('gaiaAssistantChat');
  readonly gaiaAssistantButtonSignal = viewChild<ElementRef>('gaiaAssistantButton');

  user$: Observable<SessionDto>;
  userCompany$: Observable<OrgContextDto>;
  environment = environment;
  menuItems$: Observable<MenuItemDto[]>;
  isEmployeeCardDisabled$: Observable<boolean>;
  userLanguage = getLocalePreference();
  version = getVersionFromBaseUri();
  lastModified = new Date(document.lastModified);
  protected readonly selectedEmployeeService = inject(SelectedEmployeeService);
  protected readonly disabledOperationsSignal = toSignal(
    inject(DisabledOperationService).getDisabledOperations(),
    { initialValue: {} },
  );
  protected readonly OperationTypeEnum = OperationTypeEnum;
  protected readonly featureFlagService = inject(FeatureFlagService);
  protected readonly compareCompanies = (a: OrgContextDto, b: OrgContextDto): boolean =>
    a?.id === b?.id;

  ngOnInit() {
    this.user$ = this.userSessionService.getSession();
    this.userCompany$ = this.userSessionService.getUserCompany();
    this.menuItems$ = this.userClient
      .getMenuItems()
      .pipe(
        map((response) => response.menuItems.sort((a, b) => a.menuSortOrder - b.menuSortOrder)),
      );
    this.isEmployeeCardDisabled$ = this.featureFlagService
      .getFlagValue('disable-post-for-employee')
      .pipe(shareReplay());
    if (this.userLanguage !== LanguagesEnum.No) {
      this.title.setTitle('Employees');
    }
  }

  changeLanguage() {
    changeLanguage();
    window.location.reload();
  }

  protected selectCompany(company: OrgContextDto): void {
    this.selectCompanyById(company.id);
  }

  private selectCompanyById(companyId: number): void {
    const version = getVersionFromUrl();
    const versionPath = version ? `/v/${version}` : '';
    const firstRouteParam = this.collectRouteParams()[0];
    const newUrl = this.router.url.split(firstRouteParam)[0];
    window.location.href = `${window.location.origin}${versionPath}/${companyId}${newUrl}`;
  }

  logout() {
    this.cookieService.deleteAll();
  }

  goToAbout() {
    window.open(`${environment.vismaAboutUrl}${this.userSessionService.companyId}/about`, '_blank');
  }

  openWalkme() {
    this.walkmeService.open();
  }

  openAssistant() {
    datadogRum.addAction('vsn_assistant_click');
    this.gaiaAssistantChatSignal()?.toggle();
  }

  collectRouteParams(): string[] {
    // Method from Angular docs: https://angular.io/api/router/ActivatedRouteSnapshot
    let params = {};
    const stack: ActivatedRouteSnapshot[] = [this.router.routerState.snapshot.root];
    while (stack.length > 0) {
      // originally stack.pop()!, but non-null assertion is forbidden by lint
      const route = stack.pop();
      params = { ...params, ...route.params };
      stack.push(...route.children);
    }
    // originally params, but only the values are needed
    return Object.values(params);
  }

  openLegacyEM(): void {
    const employeeNumber = this.selectedEmployeeService.employeeNumberSignal();
    datadogRum.addAction('legacy_employee_click_header');
    const url = employeeNumber
      ? `${environment.vismaEmpmanUrl}#/${this.userSessionService.companyId}/employee/${employeeNumber}`
      : `${environment.vismaEmpmanUrl}#/${this.userSessionService.companyId}`;
    window.open(url, '_blank')?.focus();
  }
}
